import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { IconButton, Stack, Typography, useMediaQuery } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TextOnlyTooltip } from '@/src/components/Tooltip/TextOnlyTooltip';
import { mediumScreen } from '@/src/shared/constants/MediaQueryConstants';
import { primaryOrange } from '@/src/styles/theme/lightThemeOptions';
import { sanitizeAssistantHtml, sanitizeAssistantText } from '@/src/utils/commonUtil';

type CopyMessageButtonProps = {
    message: string;
    html?: string;
};

export const CopyMessageButton = ({ message, html }: CopyMessageButtonProps) => {
    const { t } = useTranslation('common');
    const [isMessageCopied, setIsMessageCopied] = useState(false);
    const isMobileScreen: boolean = useMediaQuery(mediumScreen);

    const handleMessageCopy = async () => {
        try {
            const cleanText = sanitizeAssistantText(message);

            if (html) {
                const cleanHtml = sanitizeAssistantHtml(html);

                await navigator.clipboard.write([
                    new ClipboardItem({
                        'text/html': new Blob([cleanHtml], { type: 'text/html' }),
                        'text/plain': new Blob([cleanText], { type: 'text/plain' }),
                    }),
                ]);
            } else {
                await navigator.clipboard.writeText(cleanText);
            }

            setIsMessageCopied(true);
            setTimeout(() => setIsMessageCopied(false), 1500);
        } catch (err) {
            console.error('Copy failed', err);
        }
    };

    return (
        <TextOnlyTooltip title={isMessageCopied ? t('action.copied') : t('action.copy')}>
            <IconButton
                size="small"
                onClick={handleMessageCopy}
                sx={{
                    padding: '8px',
                    color: isMessageCopied ? primaryOrange : 'disabled',
                }}
            >
                {isMessageCopied ? (
                    <Stack direction="row" alignItems="center" gap="4px">
                        <CheckIcon fontSize="small" />
                        {isMobileScreen && (
                            <Typography variant="body2">{t('action.copied')}</Typography>
                        )}
                    </Stack>
                ) : (
                    <ContentCopyIcon fontSize="small" />
                )}
            </IconButton>
        </TextOnlyTooltip>
    );
};
